import * as Sentry from '@sentry/browser';

import { requiresTwoGuardians } from '.';
import { PassengerModel } from '../../models/PassengerModel';
import { PersonModel } from '../../models/PersonModel';
import {
  createPerson,
  deletePerson,
  updatePerson,
} from '../../services/resources/persons';
import { ClaimModel } from '../../models/ClaimModel';

interface LegalGuardianHandleSubmitProps {
  minorPassenger: PassengerModel;
  legalGuardians: PersonModel[];
  bearerToken?: string;
  legalGuardianValues: any;
  guardianIndex: string;
  claim: ClaimModel;
  setSubmitting: (state: boolean) => any;
  handleError: (state: boolean) => any;
  navigateNext: () => void;
}

export async function legalGuardianHandleSubmit(
  legalGuardianHandleSubmitData: LegalGuardianHandleSubmitProps
) {
  const {
    minorPassenger,
    legalGuardians,
    bearerToken,
    legalGuardianValues,
    guardianIndex,
    claim,
    setSubmitting,
    handleError,
    navigateNext,
  } = legalGuardianHandleSubmitData;
  // Variable to use if we get an error for guardian's birth date
  let legalGuardianBirhtdayError = false;
  const { has_single_legal_guardian, ...values } = legalGuardianValues;

  // Add headers for shareable pages
  const config = bearerToken
    ? {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
      }
    : null;

  /**
   * First if condition is when we have two legal guardians
   */
  if (!has_single_legal_guardian) {
    // Check if there are already dbLegalGuardians for that minor passenger
    const currentGuardian = values?.id
      ? await updateDBLegalGuardianPerson(values).catch((error: any) => {
          setSubmitting(false);
          handleError(error);
          legalGuardianBirhtdayError = true;
        })
      : await createPerson(
          {
            ...values,
            ...(values?.country_id && {
              country: { id: values.country_id },
            }),
            ...(values?.country_of_birth_id && {
              countryOfBirth: { id: values.country_of_birth_id },
            }),
            ...(minorPassenger?.person?.id && {
              wards: [{ id: minorPassenger?.person?.id }],
            }),
          },
          config
        ).catch((error: any) => {
          setSubmitting(false);
          handleError(error);
          legalGuardianBirhtdayError = true;
        });
    localStorage.setItem(
      `yo:Legal-Guardian-Signature-${minorPassenger.id}-${guardianIndex}`,
      JSON.stringify({
        id: currentGuardian?.id,
        sign: legalGuardianValues?.signature,
      })
    );
  } else {
    // Delete second signature from localStorage
    localStorage.removeItem(
      `yo:Legal-Guardian-Signature-${minorPassenger.id}-2`
    );
    // Update the first legal Guardian to a single
    const firstLegalGuardian = legalGuardians[0];

    const {
      // eslint-disable-next-line
      has_single_legal_guardian: hasSingleLegalGuardian,
      ...firstGuardianvalues
    } = firstLegalGuardian;

    await updateDBLegalGuardianPerson(firstGuardianvalues);
    await updatePerson({
      personId: minorPassenger?.person?.id,
      // tslint:disable-next-line:no-shadowed-variable
      values: {
        has_single_legal_guardian,
      },
      config,
    });
    // Then delete the second legalGuardian person
    if (legalGuardians[1]) {
      // TODO: This function works but check here after that function there is a desirialization error with attributes
      await deletePerson({
        personId: legalGuardians[1]?.id,
        config,
      });
    }
  }

  async function updateDBLegalGuardianPerson(legalGuardianPerson: PersonModel) {
    return updatePerson({
      personId: legalGuardianPerson?.id,
      values: {
        ...legalGuardianPerson,
        ...(legalGuardianPerson?.country_id && {
          country: { id: legalGuardianPerson.country_id },
        }),
        ...(legalGuardianPerson?.country_of_birth_id && {
          countryOfBirth: { id: legalGuardianPerson?.country_of_birth_id },
        }),
        ...(minorPassenger?.person?.id && {
          wards: [{ id: minorPassenger?.person?.id }],
        }),
      },
      config,
    });
  }

  // Error occured in the form in the birth date field, so return now and
  // not allow to proceed unitl user fix the birth date issue
  if (legalGuardianBirhtdayError) return;

  if (
    Number(guardianIndex) === 2 ||
    !requiresTwoGuardians(claim?.placeOfJurisdiction?.id)
  ) {
    const signaturesToOwners = [
      localStorage.getItem(
        `yo:Legal-Guardian-Signature-${minorPassenger.id}-1`
      ) ?? null,
      localStorage.getItem(
        `yo:Legal-Guardian-Signature-${minorPassenger.id}-2`
      ) ?? null,
    ]
      .filter(Boolean)
      // @ts-ignore
      .map(JSON.parse);

    // Log to sentry when number of legal guardians and signatures don't match
    if (
      (!legalGuardianValues.requiresTwoGuardians &&
        signaturesToOwners.length === 2) ||
      (legalGuardianValues.requiresTwoGuardians &&
        !legalGuardianValues.has_single_legal_guardian &&
        signaturesToOwners.length === 1)
    ) {
      // @ts-ignore
      Sentry?.captureMessage(
        `LegalGuardian Signatures ${signaturesToOwners.length}/${
          legalGuardianValues.requiresTwoGuardians &&
          !legalGuardianValues.has_single_legal_guardian
            ? '2'
            : '1'
        }`,
        {
          level: Sentry.Severity.Info,
          extra: {
            claim,
            legalGuardianValues,
            legalGuardians,
            signaturesToOwners,
          },
        }
      );
    }
    try {
      // Iterate over the signature map and add more signatures to the 2 letters if neccessary
      Promise.all(
        signaturesToOwners.map(
          async (signToOwner: { id: string; sign: string | {} }) => {
            await updatePerson({
              personId: signToOwner?.id,
              values: {
                signature: signToOwner?.sign,
              },
              config,
            });
          }
        )
      ).then(navigateNext);
      // tslint:disable-next-line:no-empty
    } catch (error) {
      throw new Error(error);
    }
  } else {
    navigateNext();
  }
}
